import { Injectable } from '@angular/core';
import { NavigationItemInterface } from '../theme/layout/admin/navigation/navigation';
import { AuthService } from './auth.service';

/**
 * Menu Permission Mapping
 * Maps menu item IDs to required privilege keys
 * Format: "category.permission" (e.g., "employeeManagement.view", "machines.add")
 */
const MENU_PERMISSION_MAP: { [key: string]: string[] } = {
  // Dashboard - usually accessible to all authenticated users
  'dashboard': [], // No permission required (empty array means accessible to all)

  // Employee Management
  'employees': ['employeeManagement.view'], // Parent menu requires view permission
  'add-employee': ['employeeManagement.add'],
  'view-employee': ['employeeManagement.view'],
  'assign-employees': ['employeeManagement.assign'],
  'attendance': ['attendance.attendanceReport'], // Attendance parent
  'attendance-report': ['attendance.attendanceReport'],
  'regularize-attendance': ['attendance.regularizationScreenAccess'],

  // Vendor Management
  'vendor': ['vendors.view'],
  'add-vendor': ['vendors.add'],
  'view-vendor': ['vendors.view'],

  // Customer Management - Using stocks permissions since customer-specific permissions may not exist
  'customer': ['stocks.stockIn', 'stocks.stockUsed'], // Show if user has any stock permission
  'add-customer': ['stocks.stockIn'], // Use stock permission for now
  'view-customers': ['stocks.stockIn'], // Use stock permission for now
  'stocks': ['stocks.stockIn'], // Customer stocks parent
  'stock-in': ['stocks.stockIn'],
  'stock-out': ['stocks.stockOut'],
  'stock-transfer': ['stocks.transfer'],
  'stock-report': ['stocks.stockIn'], // Use stockIn permission
  'stock-availability-report': ['stocks.stockIn'], // Use stockIn permission
  'machine-readings': ['machineReading.view'], // Use machineReading.view permission
  'return-stock': ['stocks.return'], // Return stock under Customer Stocks

  // Machine Management
  'machines': ['machines.view'],
  'add-machine': ['machines.add'],
  'view-machines': ['machines.view'],
  'assign-machine-to-customer': ['machines.view'], // Use view permission for now, can be changed to machines.access if needed

  // Product Management
  'products': ['products.view'],
  'add-product': ['products.add'],
  'view-products': ['products.view'],
  'product-threshold-settings': ['products.view'],

  // Internal Stocks
  'internal-stocks': ['stocks.stockIn'],
  'delivery-to-customer': ['stocks.stockOut'],
  'internal-stock-in': ['stocks.stockIn'],
  'internal-stock-availability-report': ['stocks.stockIn'], // Use stockIn permission
  'return-stocks': ['stocks.return'], // Return stocks under Internal Stocks

  // File Upload
  'file-upload': ['fileUpload.access'],

  // Deleted Records (Backup & Restore) - show only if user has backup or restore permission
  'deleted-records': ['settings.backup', 'settings.restore'],

  // Settings - Using role.view as fallback since settings.view might not exist
  'settings': ['role.view', 'products.view'], // Show if user can view roles or products
  'stock-threshold-settings': ['products.view'], // Use products permission
  'roles': ['role.view'],
};

@Injectable({
  providedIn: 'root'
})
export class MenuFilterService {
  constructor(private authService: AuthService) {}

  /**
   * Filter navigation items based on user privileges
   * @param navigationItems Original navigation items
   * @returns Filtered navigation items
   */
  filterNavigationItems(navigationItems: NavigationItemInterface[]): NavigationItemInterface[] {
    const privileges = this.authService.getPrivileges();
    
    // If no privileges, show only dashboard (default)
    if (!privileges || privileges.length === 0) {
      const dashboardItems = navigationItems.filter(item => item.id === 'dashboard');
      return dashboardItems;
    }

    const filteredItems = navigationItems
      .map(item => this.filterNavigationItem(item, privileges))
      .filter(item => item !== null) as NavigationItemInterface[];

    return filteredItems;
  }

  /**
   * Filter a single navigation item and its children
   * @param item Navigation item to filter
   * @param privileges User privileges
   * @returns Filtered navigation item or null if not accessible
   */
  private filterNavigationItem(
    item: NavigationItemInterface,
    privileges: string[]
  ): NavigationItemInterface | null {
    // If item has no ID, skip permission check (shouldn't happen, but safety first)
    if (!item.id) {
      return item;
    }

    // Get required privileges for this menu item
    const requiredPrivileges = MENU_PERMISSION_MAP[item.id] || [];

    // If no permission mapping exists, show the item (backward compatibility)
    if (requiredPrivileges.length === 0) {
      // Check if it's a parent with children - if so, check children
      if (item.children && item.children.length > 0) {
        const filteredChildren = this.filterChildren(item.children, privileges);
        if (filteredChildren.length > 0) {
          return { ...item, children: filteredChildren };
        }
        return null; // No accessible children, hide parent
      }
      return item; // No children, show item
    }

    // Check if user has required privileges
    const hasAccess = this.authService.hasAnyPrivilege(requiredPrivileges);
    
    if (!hasAccess) {
      // If parent doesn't have access, check if any children do
      if (item.children && item.children.length > 0) {
        const filteredChildren = this.filterChildren(item.children, privileges);
        if (filteredChildren.length > 0) {
          // Parent doesn't have direct access, but children do - show parent with filtered children
          return { ...item, children: filteredChildren };
        }
      }
      return null; // No access
    }

    // User has access - process children if any
    if (item.children && item.children.length > 0) {
      const filteredChildren = this.filterChildren(item.children, privileges);
      return { ...item, children: filteredChildren };
    }

    return item;
  }

  /**
   * Filter children navigation items
   * @param children Array of child navigation items
   * @param privileges User privileges
   * @returns Filtered children
   */
  private filterChildren(
    children: NavigationItemInterface[],
    privileges: string[]
  ): NavigationItemInterface[] {
    return children
      .map(child => this.filterNavigationItem(child, privileges))
      .filter(child => child !== null) as NavigationItemInterface[];
  }

  /**
   * Check if a menu item is accessible
   * @param menuId Menu item ID
   * @returns True if accessible
   */
  isMenuItemAccessible(menuId: string): boolean {
    const requiredPrivileges = MENU_PERMISSION_MAP[menuId] || [];
    
    // If no permission mapping, allow access (backward compatibility)
    if (requiredPrivileges.length === 0) {
      return true;
    }

    return this.authService.hasAnyPrivilege(requiredPrivileges);
  }

  /**
   * Get all accessible menu IDs
   * @returns Array of accessible menu IDs
   */
  getAccessibleMenuIds(): string[] {
    const privileges = this.authService.getPrivileges();
    const allMenuIds = Object.keys(MENU_PERMISSION_MAP);
    
    return allMenuIds.filter(menuId => {
      const requiredPrivileges = MENU_PERMISSION_MAP[menuId] || [];
      return requiredPrivileges.length === 0 || this.authService.hasAnyPrivilege(requiredPrivileges);
    });
  }
}
